import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import { GridToolbar } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import '../App.css';
import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import MarketsModal, { useMarkets, buildMarkets } from '../components/MarketsModal';
import { useOddsFormat, fmtPrice } from '../oddsFormat';
import { fetchDeepLink, openDeepLink, buildPrizePicksSlipUrl } from '../deeplinks';
import { TRACKING_API_URL } from '../config';
import { withAuthQuery } from '../auth';
import logger from '../logger';
import PickemTutorial from './PickemTutorial';
import {
  PICKEM_PLATFORMS, PAYOUTS_CHECKED, platformByLabel, platformForBook,
  pickemMultiplierLookup, slipTypeOptions, legBreakEven, referenceBreakEven, slipResult, dedupePickemRows,
} from '../pickemMath';

// Pick'em math (platforms, payout tables, pick multipliers, breakevens, slip
// payout/EV) lives in pickemMath.js so every number on this page comes from
// one model.

// "All" means every current option is selected, not just the same count: a
// stale selection can match the option count while matching none of them.
const allSelected = (sel, opts) => opts.length > 0 && opts.every(o => sel.includes(o));
const sameMembers = (a, b) => a.length === b.length && allSelected(a, b);

// Expanded-row panel: regular books' prices for the same player/market/side.
// The grid calls getDetailPanelContent for every row (~7k) whenever that
// function changes, so it stays stable and returns this component; only panels
// that are open read the current odds, through this context.
const AssociatedBetsContext = React.createContext({ getAssociatedBets: () => [], bookLogos: {} });

const DetailPanelItem = React.memo(({ bet, bookLogo }) => {
  const oddsFormat = useOddsFormat();  // panel is isolated from parent re-renders
  const side = bet.side === 'Over' ? 'o' : bet.side === 'Under' ? 'u' : '';
  const line = bet.points != null ? bet.points : '';
  let displayOdds = bet.price != null ? fmtPrice(bet.price, oddsFormat) : 'N/A';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: '0 11px',
        border: 'none',
        borderRadius: '4px',
        width: '100%',
      }}
    >
      <Box sx={{ mr: 0.5, transform: 'translateY(3px)' }}>
        <img
        src={bookLogo || ''}
        alt="Book Logo"
        style={{ width: 44, height: 44, objectFit: 'contain' }}
        loading="lazy"
        onError={(e) => {
          e.target.style.display = 'none'; // Hide broken images
        }}
      />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap'}}>
        <Typography
          variant="body2"
          sx={{
            color: '#ffffff',
            mr: 0.5,
            whiteSpace: 'nowrap',
            lineHeight: '44px',
            fontSize: '0.875rem',
          }}
        >
          {(side + line) || '-'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#ffffff',
            whiteSpace: 'nowrap',
            lineHeight: '44px',
            fontSize: '0.875rem',
          }}
        >
          {displayOdds}
        </Typography>
      </Box>
    </Box>
  );
});

const AssociatedBetsPanel = ({ row }) => {
  const { getAssociatedBets, bookLogos } = React.useContext(AssociatedBetsContext);
  const associatedBets = getAssociatedBets(row);

  if (!associatedBets.length) {
    return (
      <Box sx={{ p: 1, color: '#ffffff' }}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>No other books have this bet.</Typography>
      </Box>
    );
  }

  const fixedItemWidth = 150;

  return (
    <Box
      sx={{
        p: '2px 0',
        backgroundColor: '#2a2a2a',
        borderRadius: '4px',
        overflowX: 'auto',
        overflowY: 'hidden',
        display: 'flex',
        flexWrap: 'nowrap',
        height: 52,
      }}
    >
      {associatedBets.map((bet, index) => (
        <Box
          key={index}
          sx={{
            width: fixedItemWidth,
            flexShrink: 0,
            flexGrow: 0,
            display: 'flex',
            alignItems: 'center',
            borderRight: '1px solid #ffffff',
            pr: '11px',
            backgroundColor: '#2a2a2a',
          }}
        >
          <DetailPanelItem bet={bet} bookLogo={bookLogos[bet.bookName]} />
        </Box>
      ))}
    </Box>
  );
};

const AddToBetSlipCell = React.memo(({ row, betSlip, setBetSlip, maxLegs }) => {
  const id = row.id;
  const isAdded = betSlip.includes(id);
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <IconButton
        onClick={() => {
          if (betSlip.length >= maxLegs && !isAdded) return;
          const updated = isAdded
            ? betSlip.filter(x => x !== id)
            : [...betSlip, id];
          setBetSlip(updated);
        }}
        disabled={betSlip.length >= maxLegs && !isAdded}
        size="small"
        sx={{ padding: 0, color: isAdded ? 'green' : 'gray', '&.Mui-disabled': { color: 'gray' } }}
      >
        {isAdded ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      </IconButton>
    </Box>
  );
});

const TrackLegCell = React.memo(({ row, isTracked, isBusy, toggleTrack }) => (
  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <IconButton
      onClick={() => toggleTrack(row, isTracked)}
      disabled={isBusy}
      size="small"
      sx={{ padding: 0, color: isTracked ? 'green' : 'gray', '&.Mui-disabled': { color: 'gray' } }}
    >
      {isTracked ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
    </IconButton>
  </Box>
));

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const PickemDataGrid = ({ userName }) => {
  const oddsFormat = useOddsFormat();
  const bets = useStoreData('odds');
  const sportsbooks = useStoreData('sportsbooks');
  const leaguesConfig = useStoreData('leagues') || [];
  const marketsConfig = useStoreData('markets') || [];
  const { bookLogos, teamMap } = useLogoMaps();
  const { marketsOpen, marketsData, marketsMeta, openMarkets, closeMarkets } = useMarkets();

  // Bet Info click -> every market for that player (props) or game (game lines).
  const handleMarketsClick = useCallback((row) => {
    if (!row?.fixture_id) return;
    const playerId = row.playerId || null;
    const markets = buildMarkets(bets, { fixtureId: row.fixture_id, playerId, bookLogos });
    const away = teamMap[row.awayTeamId]?.abbreviation || '';
    const home = teamMap[row.homeTeamId]?.abbreviation || '';
    openMarkets({
      title: playerId
        ? (row.playerName || '')
        : (row.selection || row.playerName || `${away} @ ${home}`),
      fixture_id: row.fixture_id,
      player_id: playerId || '',
      league: row.league || row.leagueName || '',
      awayTeam: away, homeTeam: home,
      awayTeamLogo: teamMap[row.awayTeamId]?.logo || '',
      homeTeamLogo: teamMap[row.homeTeamId]?.logo || '',
      eventStart: row.eventStart,
      bookLogos,
    }, markets);
  }, [bets, bookLogos, teamMap, openMarkets]);

  // State
  const [sortModel, setSortModel] = useState([{ field: 'calculatedEV', sort: 'desc' }]);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [leagueFilter, setLeagueFilter] = useState(() => {
    const saved = localStorage.getItem('pickemLeagueFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [betTypeFilter, setBetTypeFilter] = useState(() => {
    const saved = localStorage.getItem('pickemBetTypeFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [bookFilter, setBookFilter] = useState(() => {
    const saved = localStorage.getItem('pickemBookFilter');
    return saved || '';
  });
  // Not persisted: the options are calendar days, so a saved list goes stale
  // overnight and would hide every game on the next visit.
  const [dateFilter, setDateFilter] = useState([]);
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const [betSlip, setBetSlip] = useState([]);
  // Phones: the slip panel sits above the table as a one-line bar; tap to expand
  const [slipOpen, setSlipOpen] = useState(false);
  const [playType, setPlayType] = useState('power'); // 'power' or 'flex'
  // Slip type the table's Breakeven/EV columns are quoted against (key from
  // slipTypeOptions); null → the platform's cheapest slip type
  const [slipTypeKey, setSlipTypeKey] = useState(null);
  const [slipLoading, setSlipLoading] = useState(false);
  // Tracked legs, by odds id. Legs are tracked one row per leg in TrackedBets,
  // same as the EV tool; a tracked slip is its legs sharing one source_key.
  const [trackedIds, setTrackedIds] = useState(() => new Set());
  const [trackingInProgress, setTrackingInProgress] = useState(() => new Set());
  const [slipTracking, setSlipTracking] = useState(false);

  // Clear bet slip when platform filter changes
  useEffect(() => {
    setBetSlip([]);
    setPlayType('power');
    setSlipTypeKey(null);
    if (bookFilter) {
      setSortModel([{ field: 'calculatedEV', sort: 'desc' }]);
    }
  }, [bookFilter]);

  // Save leagueFilter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pickemLeagueFilter', JSON.stringify(leagueFilter));
  }, [leagueFilter]);

  // Save betTypeFilter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pickemBetTypeFilter', JSON.stringify(betTypeFilter));
  }, [betTypeFilter]);

  // Save bookFilter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('pickemBookFilter', bookFilter);
  }, [bookFilter]);

  // One-time cleanup of the date list older builds saved
  useEffect(() => {
    localStorage.removeItem('pickemDateFilter');
  }, []);

  // Process bets and filter for pick'em bets using sportsbooks config
  // ── Pickem book list from sportsbooks config (pickem=1) ──
  const pickemBooks = useMemo(() => {
    return sportsbooks.filter(sb => sb.pickem).map(sb => sb.name).filter(Boolean).sort();
  }, [sportsbooks]);

  const pickemBookNames = useMemo(() => new Set(pickemBooks), [pickemBooks]);

  // Slip types for the selected platform (cheapest breakeven first) and the one
  // the table is quoted against
  const slipOptions = useMemo(() => slipTypeOptions(platformByLabel(bookFilter)), [bookFilter]);
  const selectedSlipOption = useMemo(
    () => slipOptions.find(o => o.key === slipTypeKey) || slipOptions[0] || null,
    [slipOptions, slipTypeKey]
  );

  // PrizePicks demons/goblins: the feed flags them (+155/-155) but doesn't
  // carry their payout, which PrizePicks only shows in its app — so their
  // multiplier is null and the breakeven/EV cells say so instead of '-'
  // (the two cells read together: "varies | check app").
  const payoutUnknownCell = (row, label) => (
    /Demons/.test(row.marketSourceName || '') && row.pickMultiplier == null
      ? (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px' }}>
          <Tooltip title="PrizePicks sets demon/goblin payouts per projection and lineup and only shows them in its app, so this pick's breakeven and EV can't be computed here." placement="top">
            <Typography sx={{ color: '#888', fontSize: '0.75rem', fontStyle: 'italic', whiteSpace: 'nowrap' }}>{label}</Typography>
          </Tooltip>
        </Box>
      )
      : null
  );

  const processedBets = useMemo(() => {
    if (!Array.isArray(bets)) {
      logger.log('Pickem bets prop is not an array:', bets);
      return [];
    }

    // Filter bets whose bookName matches a pickem sportsbook (one row per line
    // per platform — see dedupePickemRows). Multipliers come from the
    // twin-aware lookup so Underdog's -137 discounts aren't read as 1x.
    const pickemRows = bets.filter(bet => pickemBookNames.has(bet.bookName));
    const multiplierFor = pickemMultiplierLookup(pickemRows);
    const pickemBets = dedupePickemRows(pickemRows, multiplierFor);

    const processed = pickemBets
      .filter(row => row.trueWin != null) // Filter out bets where Model% is null
      .map((row, idx) => {
        const id = row.id ?? `${idx}-${Date.now()}`;
        const displayName = row.playerName?.trim() || row.selection || '';
        let side = row.side || '';
        if (side === 'Over') side = 'o';
        if (side === 'Under') side = 'u';

        // Breakeven = the slip type's breakeven ÷ this pick's multiplier.
        // Selected platform uses the chosen slip type; others their cheapest.
        // breakEvenWin is overwritten so the columns and tracking
        // all use it; the feed's value stays as feedBreakEvenWin.
        const rowPlatform = platformForBook(row.bookName);
        const slipOption = rowPlatform?.label === bookFilter
          ? selectedSlipOption
          : slipTypeOptions(rowPlatform)[0];
        const pickMultiplier = multiplierFor(row);
        const adjustedBE = legBreakEven(slipOption, pickMultiplier);

        let calculatedEV = null;
        if (row.trueWin != null && adjustedBE != null && adjustedBE > 0) {
          calculatedEV = (row.trueWin / adjustedBE) - 1;
        }

        // EV at the platform's cheapest slip type — a fixed reference for the
        // too-good-to-be-true filter, so the rows shown don't change with the
        // "Breakeven for" selection (which only rescales EVs)
        const refBE = referenceBreakEven(rowPlatform, pickMultiplier);
        const referenceEV = row.trueWin != null && refBE > 0 ? (row.trueWin / refBE) - 1 : null;

        // Derive betMarketId for associated bets grouping (cross-line: groups ALL lines for same player/market)
        // Player props: use playerId (stable across line changes)
        // Game lines: use fixture + market + side
        let expandMarketId = null;
        if (row.fixture_id && row.market_id) {
          if (row.playerId) {
            expandMarketId = `${row.fixture_id}||${row.market_id}||${row.playerId}||${row.side || ''}`;
          } else {
            expandMarketId = `${row.fixture_id}||${row.market_id}||||${row.side || ''}`;
          }
        }
        
        return {
          ...row,
          id,
          playerName: displayName,
          sidepoints: `${side}${row.points || ''}`.trim(),
          calculatedEV,
          referenceEV,
          feedBreakEvenWin: row.breakEvenWin,
          breakEvenWin: adjustedBE,
          pickMultiplier,
          // Map fields from transformRecord format to Pickem expected format
          marketSourceName: row.bookName || '',
          leagueName: row.league || '',
          betTypeName: row.marketName || row.market || '',
          eventShortName: `${teamMap[row.awayTeamId]?.abbreviation || teamMap[row.awayTeamId]?.name || '?'}@${teamMap[row.homeTeamId]?.abbreviation || teamMap[row.homeTeamId]?.name || '?'}`,
          expandMarketId,
        };
      });

    logger.log('Processed bets for Pickem:', processed.length, 'items');
    logger.log('Sample processed bets:', processed.slice(0, 3).map(bet => ({
      id: bet.id,
      leagueName: bet.leagueName,
      betTypeName: bet.betTypeName,
      marketSourceName: bet.marketSourceName,
      eventShortName: bet.eventShortName,
      playerName: bet.playerName,
      side: bet.side,
      points: bet.points,
      trueWin: bet.trueWin,
      betMarketId: bet.expandMarketId,
    })));

    return processed;
  }, [bets, pickemBookNames, teamMap, bookFilter, selectedSlipOption]);

  // Date helpers for the date slate filter (uses local timezone so slate boundaries match what the user sees)
  const getLocalDateKey = (d) => {
    const date = new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const formatDateLabel = (key) => {
    // key is YYYY-MM-DD; build a Date in local time for nice formatting
    const [y, m, d] = key.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const dtMid = new Date(dt); dtMid.setHours(0,0,0,0);
    if (dtMid.getTime() === today.getTime()) return `Today (${dt.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })})`;
    if (dtMid.getTime() === tomorrow.getTime()) return `Tomorrow (${dt.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })})`;
    return dt.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Compute filter options directly
  const filterOptions = useMemo(() => {
    // Leagues: prefer server config, fall back to data-derived
    const leagues = leaguesConfig.length > 0
      ? leaguesConfig.map(l => l.name || l.id).filter(Boolean)
      : [...new Set(processedBets.map(bet => bet.leagueName).filter(Boolean))];

    // Bet types: prefer server config, fall back to data-derived
    const betTypes = marketsConfig.length > 0
      ? marketsConfig.map(m => m.name || m.id).filter(Boolean)
      : [...new Set(processedBets.map(bet => bet.betTypeName).filter(Boolean))];

    // Dates: derive from event start times in local timezone, sorted ascending (YYYY-MM-DD strings sort correctly as text)
    const dates = [...new Set(
      processedBets
        .filter(bet => bet.eventStart)
        .map(bet => getLocalDateKey(bet.eventStart))
    )].sort();

    return {
      leagues,
      betTypes,
      dates,
    };
  }, [processedBets, leaguesConfig, marketsConfig]);

  // Set default bookFilter based on available platforms
  useEffect(() => {
    if (!bookFilter) {
      setBookFilter(PICKEM_PLATFORMS[0].label);
    }
  }, [bookFilter]);

  // Track whether the user has manually interacted with each filter.
  // Without this guard, polling-driven filterOptions changes would re-fill
  // any filter the user has cleared on the next poll cycle. handleFilterChange
  // flips the corresponding key when the user touches a dropdown.
  const userTouchedFiltersRef = useRef({ leagues: false, betTypes: false, dates: false });

  useEffect(() => {
    if (filterOptions.leagues.length > 0
        && !userTouchedFiltersRef.current.leagues
        && leagueFilter.length === 0) {
      setLeagueFilter(filterOptions.leagues);
    }
    if (filterOptions.betTypes.length > 0
        && !userTouchedFiltersRef.current.betTypes
        && betTypeFilter.length === 0) {
      setBetTypeFilter(filterOptions.betTypes);
    }
    // Until the user picks dates, follow every day on offer, so a new day
    // showing up (or midnight rolling over) doesn't leave it unchecked.
    if (filterOptions.dates.length > 0
        && !userTouchedFiltersRef.current.dates
        && !sameMembers(dateFilter, filterOptions.dates)) {
      setDateFilter(filterOptions.dates);
    }
  }, [filterOptions]);

  // Filter setters
  const debouncedSetLeagueFilter = setLeagueFilter;
  const debouncedSetBetTypeFilter = setBetTypeFilter;
  const debouncedSetDateFilter = setDateFilter;

  // Compute filtered bets
  const filteredBets = useMemo(() => {
    if (!processedBets.length) {
      logger.log('No processed bets to filter');
      return [];
    }

    let filtered = processedBets;

    // Hide rows above 8% EV (usually stale or bad data), measured at the
    // platform's cheapest slip type so the set doesn't depend on "Breakeven for"
    filtered = filtered.filter(bet => bet.referenceEV == null || bet.referenceEV <= 0.08);

    if (leagueFilter.length > 0) {
      const leagueSet = new Set(leagueFilter);
      filtered = filtered.filter(bet => !bet.leagueName || leagueSet.has(bet.leagueName));
    }
    if (betTypeFilter.length > 0) {
      const betTypeSet = new Set(betTypeFilter);
      filtered = filtered.filter(bet => !bet.betTypeName || betTypeSet.has(bet.betTypeName));
    }
    if (dateFilter.length > 0) {
      const dateSet = new Set(dateFilter);
      filtered = filtered.filter(bet => !bet.eventStart || dateSet.has(getLocalDateKey(bet.eventStart)));
    }
    if (bookFilter) {
      const platform = PICKEM_PLATFORMS.find(p => p.label === bookFilter);
      if (platform) {
        filtered = filtered.filter(bet => !bet.marketSourceName || bet.marketSourceName.startsWith(platform.prefix));
      }
    }

    logger.log('Filtered bets for Pickem table:', filtered.length, 'items');
    logger.log('Sample filtered bets:', filtered.slice(0, 3).map(bet => ({
      id: bet.id,
      leagueName: bet.leagueName,
      betTypeName: bet.betTypeName,
      marketSourceName: bet.marketSourceName,
    })));
    return filtered;
  }, [
    processedBets,
    leagueFilter,
    betTypeFilter,
    bookFilter,
    dateFilter,
  ]);

  // Compute bets in the bet slip
  const betSlipBets = useMemo(() => {
    return processedBets.filter(bet => betSlip.includes(bet.id));
  }, [processedBets, betSlip]);

  // Slip odds, payout and EV for the selected platform + play type (pickemMath.slipResult)
  const slipWinInfo = useMemo(() => {
    const platform = platformByLabel(bookFilter);
    const maxLegs = platform?.maxLegs || 8;
    const n = betSlipBets.length;
    const flexSizes = Object.keys(platform?.flex || {}).map(Number);
    const hasFlex = flexSizes.length > 0;
    const minFlexLegs = hasFlex ? Math.min(...flexSizes) : 99;
    const flexOffered = !!platform?.flex?.[n];

    const result = slipResult(platform, playType, betSlipBets.map(bet => ({ p: bet.trueWin, m: bet.pickMultiplier })));

    // Picks on the same player + market (e.g. Over and Under of one prop) can't
    // both be entered, and the hit math assumes independent picks
    const seen = new Map();
    const conflicts = [];
    betSlipBets.forEach(bet => {
      const key = `${bet.fixture_id}|${bet.market_id}|${bet.playerId || bet.selection || ''}`;
      if (seen.has(key)) conflicts.push(`${bet.playerName} — ${bet.betTypeName}`);
      seen.set(key, true);
    });

    return {
      ...result,
      odds: n && result.fairOdds != null ? fmtPrice(result.fairOdds, oddsFormat) : '-',
      maxLegs, hasFlex, minFlexLegs, flexOffered, conflicts,
    };
  }, [betSlipBets, bookFilter, playType, oddsFormat]);

  // Auto-reset to Power if the slip size isn't offered as Flex
  useEffect(() => {
    if (playType === 'flex' && betSlipBets.length > 0 && !slipWinInfo.flexOffered) {
      setPlayType('power');
    }
  }, [betSlipBets.length, playType, slipWinInfo.flexOffered]);

  // Open combined betslip in PrizePicks app
  const openSlipInApp = useCallback(async () => {
    if (betSlipBets.length < 2) return;
    setSlipLoading(true);
    try {
      const isPrizePicks = bookFilter === 'PrizePicks';
      if (isPrizePicks) {
        const url = await buildPrizePicksSlipUrl(betSlipBets.map(b => b.id));
        if (url) openDeepLink(url);
      }
    } finally {
      setSlipLoading(false);
    }
  }, [betSlipBets, bookFilter]);

  // ── Tracking (REST, shared with EV tool / Bet Tracker) ──
  useEffect(() => {
    if (!userName) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(withAuthQuery(`${TRACKING_API_URL}?username=${encodeURIComponent(userName)}`));
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setTrackedIds(new Set((data.tracked_bets || []).map(b => b.odds_id)));
      } catch (err) {
        console.error('[TRACKED-BETS] Failed to fetch:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [userName]);

  const markBusy = useCallback((ids, busy) => {
    setTrackingInProgress(prev => {
      const next = new Set(prev);
      ids.forEach(id => (busy ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const trackLeg = useCallback(async (bet, sourceKey = null) => {
    const away = teamMap[bet.awayTeamId];
    const home = teamMap[bet.homeTeamId];
    const resp = await fetch(withAuthQuery(TRACKING_API_URL), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username:       userName,
        odds_id:        bet.id,
        sportsbook:     bet.bookName,
        fixture_id:     bet.fixture_id,
        event_name:     `${away?.abbreviation || away?.name || '?'} @ ${home?.abbreviation || home?.name || '?'}`,
        event_start:    bet.eventStart,
        league:         bet.league,
        player_name:    bet.playerName,
        player_id:      bet.playerId,
        market_id:      bet.market_id,
        market_display: bet.betTypeName,
        side:           bet.side,
        points:         bet.points,
        price:          bet.price,
        // The page's EV, i.e. on the parlay-adjusted breakeven for multiplier legs
        roi:            bet.calculatedEV,
        home_team:      home?.name || '',
        away_team:      away?.name || '',
        home_logo:      home?.logo || '',
        away_logo:      away?.logo || '',
        source_type:    'pickem',
        source_key:     sourceKey,
      }),
    });
    // 409 = already tracked, which is the state we wanted anyway
    if (!resp.ok && resp.status !== 409) throw new Error(`Track failed: HTTP ${resp.status}`);
    setTrackedIds(prev => new Set(prev).add(bet.id));
  }, [userName, teamMap]);

  const untrackLeg = useCallback(async (oddsId) => {
    const resp = await fetch(
      withAuthQuery(`${TRACKING_API_URL}/${encodeURIComponent(oddsId)}?username=${encodeURIComponent(userName)}`),
      { method: 'DELETE' }
    );
    if (!resp.ok && resp.status !== 404) throw new Error(`Untrack failed: HTTP ${resp.status}`);
    setTrackedIds(prev => { const next = new Set(prev); next.delete(oddsId); return next; });
  }, [userName]);

  const toggleTrack = useCallback(async (row, isTracked) => {
    if (!userName || trackingInProgress.has(row.id)) return;
    markBusy([row.id], true);
    try {
      if (isTracked) await untrackLeg(row.id);
      else await trackLeg(row);
    } catch (err) {
      console.error('[TRACK] Error:', err.message);
    } finally {
      markBusy([row.id], false);
    }
  }, [userName, trackingInProgress, markBusy, trackLeg, untrackLeg]);

  // Track every untracked leg in the slip under one slip id, so Bet Tracker
  // can group them back into the slip later. Legs tracked on their own
  // beforehand keep their original (slip-less) row.
  const slipUntracked = useMemo(
    () => betSlipBets.filter(bet => !trackedIds.has(bet.id)),
    [betSlipBets, trackedIds]
  );

  const trackSlip = useCallback(async () => {
    if (!userName || slipUntracked.length === 0) return;
    const slipKey = `slip-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const ids = slipUntracked.map(bet => bet.id);
    setSlipTracking(true);
    markBusy(ids, true);
    try {
      const results = await Promise.allSettled(slipUntracked.map(bet => trackLeg(bet, slipKey)));
      results.forEach(r => { if (r.status === 'rejected') console.error('[TRACK] Slip leg error:', r.reason?.message); });
    } finally {
      markBusy(ids, false);
      setSlipTracking(false);
    }
  }, [userName, slipUntracked, markBusy, trackLeg]);

  useEffect(() => {
    setCurrentPage(0);
  }, [
    leagueFilter,
    betTypeFilter,
    bookFilter,
    processedBets,
  ]);

  const handleFilterChange = (e, options, setter, debouncedSetter, touchKey) => {
    if (touchKey && userTouchedFiltersRef.current.hasOwnProperty(touchKey)) {
      userTouchedFiltersRef.current[touchKey] = true;
    }
    const val = e.target.value;
    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newValue = allSelected(prev, options) ? [] : options;
        debouncedSetter(newValue);
        return newValue;
      });
    } else {
      debouncedSetter(val);
    }
  };

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (allSelected(sel, opts)) return 'All';
    return sel.length > 1 ? `Multiple (${sel.length})` : sel;
  };

  // Static column definitions
  const staticColumns = [
    {
      field: 'eventShortName',
      headerName: 'Event',
      flex: 2,
      minWidth: 180,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Event
        </Typography>
      ),
      renderCell: (params) => {
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        const [awayTeam, homeTeam] = params.value.split('@');
        const leagueName = params.row.leagueName || '';
        
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {/* League name */}
              {leagueName && (
                <>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0 }}>
                    {leagueName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mx: 0.5 }}>
                   |
                  </Typography>
                </>
              )}
              {/* Away team */}
              {teamMap[params.row.awayTeamId]?.logo && (
                <img
                  src={teamMap[params.row.awayTeamId]?.logo}
                  alt={`${awayTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{awayTeam}</Typography>
              <Typography sx={{ fontSize: '12px', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {/* Home team */}
              {teamMap[params.row.homeTeamId]?.logo && (
                <img
                  src={teamMap[params.row.homeTeamId]?.logo}
                  alt={`${homeTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{homeTeam}</Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      flex: 2,
      minWidth: 200,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Bet Info
        </Typography>
      ),
      renderCell: (params) => {
        const playerName = params.row.playerName || '';
        const betTypeName = params.row.betTypeName || '';
        return (
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Tooltip title={playerName} placement="top" arrow>
              <Typography
                onClick={(e) => { e.stopPropagation(); handleMarketsClick(params.row); }}
                sx={{
                  fontSize: '0.875rem',
                  paddingLeft: '8px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {playerName}
              </Typography>
            </Tooltip>
            <Tooltip title={betTypeName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  paddingLeft: '8px',
                  color: '#BBBBBB',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {betTypeName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    { 
      field: 'sidepoints', 
      headerName: 'Line', 
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Line
        </Typography>
      ),
      renderCell: params => {
        const sidepoints = params.value || '';
        let prefix = sidepoints[0] || ''; // 'u' or 'o'
        let numberStr = sidepoints.slice(1); // '250.5' or '20' or '2.5'
        let formattedNumber = '';
        let paddedPrefix = '      '; // Default spaces for alignment
    
        if (sidepoints && numberStr) {
          const numberParts = numberStr.split('.');
          const integerPart = numberParts[0] || ''; // '250' or '20' or '2'
          const decimalPart = numberParts[1] || '0'; // '5' or undefined (becomes '0')
          
          if (integerPart) { // Valid number part
            formattedNumber = `${integerPart}.${decimalPart}`; // Always includes decimal
            
            // Calculate padding based on the FORMATTED number length (which always includes .X)
            const formattedLength = formattedNumber.length;
            const maxLength = 6; // Accommodate numbers like "250.5" (5 chars + 1 for safety)
            const spaces = maxLength - formattedLength;
            paddedPrefix = spaces > 0 ? ' '.repeat(spaces) + prefix : prefix;
          }
        }
    
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', whiteSpace: 'pre', fontFamily: 'monospace' }}>
              {sidepoints ? `${paddedPrefix}${formattedNumber}` : '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'trueWin',
      headerName: 'Model%',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Model%
        </Typography>
      ),
      valueFormatter: v => (v == null ? '' : `${(v * 100).toFixed(1)}%`)
    },
   {
      field: 'breakEvenWin',
      headerName: 'Breakeven',
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <Tooltip title="Win rate this pick needs to break even, for the slip type picked under 'Breakeven for' (a pick's breakeven depends on the slip you play it in). Boosted and discounted picks are scaled by their multiplier." placement="top">
          <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
            Breakeven
          </Typography>
        </Tooltip>
      ),
      renderCell: (params) => {
        const breakEvenWin = params.row.breakEvenWin;
        if (breakEvenWin == null) {
          return payoutUnknownCell(params.row, 'varies') || (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px' }}>
              <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>-</Typography>
            </Box>
          );
        }
        
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px' }}>
            <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>
              {`${(breakEvenWin * 100).toFixed(1)}%`}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'calculatedEV',
      headerName: 'EV',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          EV
        </Typography>
      ),
      renderCell: (params) => {
        const trueWin = params.row.trueWin;
        const breakEvenWin = params.row.breakEvenWin;
        
        // Calculate EV only if both values exist and breakEvenWin > 0
        if (trueWin == null || breakEvenWin == null || breakEvenWin <= 0) {
          return payoutUnknownCell(params.row, 'check app') || (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px' }}>
              <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>-</Typography>
            </Box>
          );
        }
        
        // Calculate EV: (Model% / Breakeven%) - 1
        const evValue = (trueWin / breakEvenWin) - 1;
        const evPercent = (evValue * 100).toFixed(1);
        
        // Color based on positive/negative EV
        const evColor = evValue > 0 ? '#00ff00' : evValue < 0 ? '#ff0000' : '#ffffff';
        
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px' }}>
            <Typography sx={{ color: evColor, fontSize: '0.875rem' }}>
              {evPercent}%
            </Typography>
          </Box>
        );
      }
    },
  ];

  // Dynamic columns
  const columns = useMemo(() => [
    ...staticColumns,
    {
      field: 'deepLink',
      headerName: '',
      sortable: false,
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      flex: 0,
      renderCell: (params) => {
        const disabled = !params.row.hasDeeplink;
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              onClick={async () => { if (!disabled) { const url = await fetchDeepLink(params.row.id); if (url) openDeepLink(url); } }}
              disabled={disabled}
              size="small"
              sx={{
                color: disabled ? '#666666' : '#00ff00',
                backgroundColor: 'transparent',
                border: 'none',
                padding: '4px 8px',
                fontSize: '0.875rem',
                minWidth: 'auto',
                '&.Mui-disabled': { color: '#666666' },
              }}
            >
              Bet
            </Button>
          </Box>
        );
      },
    },
    {
      field: 'addToBetSlip',
      headerName: 'Slip',
      sortable: false,
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      flex: 0,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Slip</Typography>
      ),
      renderCell: p => (
        <AddToBetSlipCell
          row={p.row}
          betSlip={betSlip}
          setBetSlip={setBetSlip}
          maxLegs={slipWinInfo.maxLegs}
        />
      ),
    },
    {
      field: 'track',
      headerName: 'Track',
      sortable: false,
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      flex: 0,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Track</Typography>
      ),
      renderCell: p => (
        <TrackLegCell
          row={p.row}
          isTracked={trackedIds.has(p.row.id)}
          isBusy={!userName || trackingInProgress.has(p.row.id)}
          toggleTrack={toggleTrack}
        />
      ),
    },
  ], [teamMap, bookLogos, betSlip, slipWinInfo.maxLegs, trackedIds, trackingInProgress, toggleTrack, userName]);

  // Regular books' prices for an expanded row: every line for the same
  // player/market/side (expandMarketId). Scanned on demand — only open panels
  // ask — and cached until the next odds update. Indexing all odds up front on
  // every update took ~750ms (276k rows) and froze the page.
  const getAssociatedBets = useMemo(() => {
    const cache = new Map();
    return (row) => {
      if (!row.expandMarketId) return [];
      if (cache.has(row.expandMarketId)) {
        return cache.get(row.expandMarketId);
      }
      const associated = [];
      bets.forEach((bet, idx) => {
        if (bet.fixture_id !== row.fixture_id || bet.market_id !== row.market_id) return;
        if ((bet.playerId || '') !== (row.playerId || '') || (bet.side || '') !== (row.side || '')) return;
        // Exclude pickem books — we want to compare against regular books
        if (pickemBookNames.has(bet.bookName)) return;
        associated.push({ ...bet, marketSourceName: bet.bookName, expandMarketId: row.expandMarketId, originalIndex: idx });
      });

      // Sort by best line then best odds (matching EVTool logic), then book name
      const isOver = associated[0]?.side === 'Over';
      associated.sort((a, b) => {
        const lineA = Number(a.points) || 0, lineB = Number(b.points) || 0;
        const priceA = a.price ?? -Infinity, priceB = b.price ?? -Infinity;
        if (lineA !== lineB) return isOver ? lineA - lineB : lineB - lineA;
        if (priceA !== priceB) return priceB - priceA;
        if (a.marketSourceName !== b.marketSourceName) return a.marketSourceName < b.marketSourceName ? -1 : 1;
        return a.originalIndex - b.originalIndex;
      });

      cache.set(row.expandMarketId, associated);
      return associated;
    };
  }, [bets, pickemBookNames]);

  // Detail panel content. Stable on purpose: the grid calls this for every row
  // each time it changes (see AssociatedBetsPanel); open panels read the
  // current odds through AssociatedBetsContext instead.
  const getDetailPanelContent = useCallback(({ row }) => <AssociatedBetsPanel row={row} />, []);
  const associatedBetsContext = useMemo(() => ({ getAssociatedBets, bookLogos }), [getAssociatedBets, bookLogos]);

  const getDetailPanelHeight = useCallback(() => 52, []);

  const handleDetailPanelExpanded = useCallback((newExpandedRowIds) => {
    setExpandedRowIds(new Set(newExpandedRowIds));
  }, []);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: { xs: 'calc(100vh - 64px)', md: '100vh' }, backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Filter Section (Dropdowns) */}
          <Box className="filter-section">
            <PickemTutorial />
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
                {/* League Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="league-filter-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
                  <Select
                    multiple
                    labelId="league-filter-label"
                    label="League"
                    notched
                    value={leagueFilter}
                    onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, debouncedSetLeagueFilter, 'leagues')}
                    renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={allSelected(leagueFilter, filterOptions.leagues)} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {filterOptions.leagues.map(o => (
                      <MenuItem key={o} value={o}>
                        <Checkbox checked={leagueFilter.includes(o)} />
                        <ListItemText primary={o} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Bet Type Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="bet-type-filter-label" shrink sx={{ color: '#ffffff' }}>Bet Type</InputLabel>
                  <Select
                    multiple
                    labelId="bet-type-filter-label"
                    label="Bet Type"
                    notched
                    value={betTypeFilter}
                    onChange={e => handleFilterChange(e, filterOptions.betTypes, setBetTypeFilter, debouncedSetBetTypeFilter, 'betTypes')}
                    renderValue={sel => renderFilterValue(sel, filterOptions.betTypes, 'Bet Type')}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={allSelected(betTypeFilter, filterOptions.betTypes)} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {filterOptions.betTypes.map(o => (
                      <MenuItem key={o} value={o}>
                        <Checkbox checked={betTypeFilter.includes(o)} />
                        <ListItemText primary={o} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Platform Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="book-filter-label" shrink sx={{ color: '#ffffff' }}>Platform</InputLabel>
                  <Select
                    labelId="book-filter-label"
                    label="Platform"
                    notched
                    value={bookFilter}
                    onChange={e => setBookFilter(e.target.value)}
                  >
                    {PICKEM_PLATFORMS.map(p => (
                      <MenuItem key={p.label} value={p.label}>
                        <ListItemText primary={p.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Slip type the Breakeven / EV columns are quoted against */}
                <FormControl data-tutorial="pickem-slip-type" variant="outlined" size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="slip-type-label" shrink sx={{ color: '#ffffff' }}>Breakeven for</InputLabel>
                    <Select
                      labelId="slip-type-label"
                      label="Breakeven for"
                      notched
                      value={selectedSlipOption?.key || ''}
                      onChange={e => setSlipTypeKey(e.target.value)}
                      renderValue={key => {
                        const o = slipOptions.find(x => x.key === key);
                        return o ? (o.key === 'perPick' ? o.label : `${o.label} (${(o.be * 100).toFixed(1)}%)`) : '';
                      }}
                    >
                      {slipOptions.map(o => (
                        <MenuItem key={o.key} value={o.key}>
                          <ListItemText
                            primary={o.label}
                            secondary={o.key === 'perPick' ? 'each pick priced on its own' : `${(o.be * 100).toFixed(1)}% per pick`}
                            secondaryTypographyProps={{ sx: { color: '#aaaaaa', fontSize: '0.75rem' } }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                {/* Date Filter */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="date-filter-label" shrink sx={{ color: '#ffffff' }}>Date</InputLabel>
                  <Select
                    multiple
                    labelId="date-filter-label"
                    label="Date"
                    notched
                    value={dateFilter}
                    onChange={e => handleFilterChange(e, filterOptions.dates, setDateFilter, debouncedSetDateFilter, 'dates')}
                    renderValue={sel => {
                      if (!sel.length) return 'Date';
                      if (allSelected(sel, filterOptions.dates)) return 'All';
                      if (sel.length === 1) return formatDateLabel(sel[0]);
                      return `Multiple (${sel.length})`;
                    }}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={allSelected(dateFilter, filterOptions.dates)} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {filterOptions.dates.map(o => (
                      <MenuItem key={o} value={o}>
                        <Checkbox checked={dateFilter.includes(o)} />
                        <ListItemText primary={formatDateLabel(o)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={0} sx={{ height: '100%' }}>
              {/* Table. Phones: below the slip bar, filling whatever the wrapped
                  filter rows and the bar leave, instead of the fixed desktop height */}
              <Box sx={{
                width: { xs: '100%', md: '75%' }, flex: { xs: 1, md: 'none' }, minHeight: 0,
                display: 'flex', flexDirection: 'column',
                '& .table-section': { height: { xs: 'auto', md: 'calc(100vh - 112px)' }, flex: { xs: 1, md: 'none' }, minHeight: 0 },
              }}>
                <Box className="table-section">
                  <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
                    <AssociatedBetsContext.Provider value={associatedBetsContext}>
                    <DataGridPro
                      rows={filteredBets}
                      columns={columns}
                      pageSize={pageSize}
                      onPageSizeChange={setPageSize}
                      rowsPerPageOptions={[50, 100, 250]}
                      pagination
                      paginationMode="client"
                      page={currentPage}
                      onPageChange={setCurrentPage}
                      rowCount={filteredBets.length}
                      sortingMode="client"
                      sortModel={sortModel}
                      onSortModelChange={newModel => setSortModel(newModel)}
                      disableSelectionOnClick
                      disableColumnFilter
                      disableColumnMenu
                      loading={false}
                      getRowId={row => row.id}
                      getRowClassName={(params) => params.row.isDetail ? 'detail-row' : ''}
                      getDetailPanelContent={getDetailPanelContent}
                      getDetailPanelHeight={getDetailPanelHeight}
                      detailPanelExpandedRowIds={expandedRowIds}
                      onDetailPanelExpandedRowIdsChange={handleDetailPanelExpanded}
                      hideFooterSelectedRowCount
                      initialState={{
                        columns: { columnVisibilityModel: { marketSourceName: false } },
                      }}
                      components={{ Toolbar: GridToolbar }}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#1e1e1e',
                          borderBottom: '2px solid #444',
                          position: 'sticky',
                          top: '56px',
                          zIndex: 2,
                        },
                        '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
   
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: '#ffffff !important',
                          userSelect: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                          color: '#ffffff !important',
                          userSelect: 'none',
                          borderRight: '1px solid rgba(255, 255, 255, 0.3)', // Column separator - increased opacity to 30%
                        },
                        '& .MuiDataGrid-cell:last-child': {
                          borderRight: 'none', // Remove border from last column
                        },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                        '& .MuiDataGrid-row.Mui-selected': {
                          backgroundColor: '#1a1a1a',
                          '&:hover': { backgroundColor: '#1a1a1a' },
                        },
                        '& .MuiDataGrid-columnSeparator': { 
                          visibility: 'visible',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': { color: '#ffffff !important' },
                        '& .MuiDataGrid-iconButtonContainer': { color: '#ffffff !important' },
                        '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                        '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                          color: '#ffffff !important',
                        },
                        '& .MuiSelect-icon': { color: '#ffffff !important' },
                        '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                        '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
                        '& .MuiDataGrid-root': {
                          overflowY: 'auto !important',
                          margin: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                          overflowY: 'auto !important',
                        },
                        '& .MuiDataGrid-main': {
                          padding: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          backgroundColor: '#1e1e1e',
                          color: '#ffffff !important',
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 1,
                          boxSizing: 'border-box',
                        },
                      }}
                      rowBuffer={10}
                      rowThreshold={5}
                    />
                    </AssociatedBetsContext.Provider>
                  </Box>
                </Box>
              </Box>
              {/* Bet Slip Panel */}
              <Box data-tutorial="pickem-slip-panel" sx={{
                width: { xs: '100%', md: '25%' }, boxSizing: 'border-box',
                // Phones: a 56px bar above the table that expands to half the screen
                order: { xs: -1, md: 0 }, flexShrink: 0,
                height: { xs: slipOpen ? '50vh' : '56px', md: 'calc(100vh - 128px)' },
                overflow: { xs: 'hidden', md: 'visible' },
                transition: 'height 150ms ease',
                // Collapsed: only the header row. The payout summary at the bottom of
                // the panel is sticky, so clipping alone would show it over the header.
                '@media (max-width:899.95px)': {
                  '& > .MuiBox-root > *:not(:first-of-type)': { display: slipOpen ? undefined : 'none' },
                },
              }}>
                <Box sx={{ backgroundColor: '#1a1a1a', borderRadius: '12px', height: '100%', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center"
                    onClick={() => setSlipOpen(o => !o)}
                    sx={{ p: 2, minHeight: '56px', boxSizing: 'border-box', cursor: { xs: 'pointer', md: 'default' } }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                      Bet Slip ({betSlipBets.length})
                      <Box component="span" sx={{ display: { xs: 'inline', md: 'none' }, color: '#888', ml: 1, fontSize: '0.8rem' }}>{slipOpen ? '\u25B2' : '\u25BC'}</Box>
                    </Typography>
                    <Button
                      variant="outlined"
                      sx={{
                        color: '#ffffff',
                        borderColor: '#ffffff',
                        fontSize: '0.8rem',
                        padding: '4px 8px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: '#ffffff',
                        },
                      }}
                      onClick={(e) => { e.stopPropagation(); setBetSlip([]); }}
                      disabled={betSlipBets.length === 0}
                    >
                      Clear Bet Slip
                    </Button>
                  </Stack>
                  {/* Power / Flex toggle — only shown for platforms with Flex support */}
                  {slipWinInfo.hasFlex && (
                    <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1 }}>
                      <Button
                        size="small"
                        variant={playType === 'power' ? 'contained' : 'outlined'}
                        onClick={() => setPlayType('power')}
                        sx={{
                          flex: 1, fontSize: '0.8rem', fontWeight: 600,
                          color: playType === 'power' ? '#000' : '#ffffff',
                          backgroundColor: playType === 'power' ? '#ffffff' : 'transparent',
                          borderColor: '#555',
                          '&:hover': { backgroundColor: playType === 'power' ? '#dddddd' : 'rgba(255,255,255,0.1)', borderColor: '#888' },
                        }}
                      >
                        Power
                      </Button>
                      <Button
                        size="small"
                        variant={playType === 'flex' ? 'contained' : 'outlined'}
                        onClick={() => setPlayType('flex')}
                        disabled={!slipWinInfo.flexOffered}
                        sx={{
                          flex: 1, fontSize: '0.8rem', fontWeight: 600,
                          color: playType === 'flex' ? '#000' : '#ffffff',
                          backgroundColor: playType === 'flex' ? '#ffffff' : 'transparent',
                          borderColor: '#555',
                          '&:hover': { backgroundColor: playType === 'flex' ? '#dddddd' : 'rgba(255,255,255,0.1)', borderColor: '#888' },
                          '&.Mui-disabled': { color: '#555', borderColor: '#333' },
                        }}
                      >
                        Flex {betSlipBets.length < slipWinInfo.minFlexLegs
                          ? `(${slipWinInfo.minFlexLegs}+ picks)`
                          : !slipWinInfo.flexOffered ? `(not for ${betSlipBets.length})` : ''}
                      </Button>
                    </Stack>
                  )}
                  <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
                    {betSlipBets.length === 0 ? (
                      <Typography sx={{ color: '#bbbbbb', textAlign: 'center', fontStyle: 'italic' }}>
                        No bets added to the slip.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {betSlipBets.map(bet => (
                          <Box
                            key={bet.id}
                            sx={{
                              p: 1.5,
                              backgroundColor: '#222222',
                              borderRadius: '8px',
                              border: '1px solid #333',
                              transition: 'background-color 0.2s',
                              '&:hover': { backgroundColor: '#2a2a2a' },
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <IconButton
                                size="small"
                                onClick={() => setBetSlip(betSlip.filter(id => id !== bet.id))}
                                sx={{ color: '#ffffff', '&:hover': { color: '#ff4444' } }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500 }}>
                                  {bet.playerName} | {bet.betTypeName} | {bet.sidepoints}
                                  {/Multipliers|Demons/.test(bet.marketSourceName || '') && bet.pickMultiplier != null && bet.pickMultiplier !== 1
                                    ? <span style={{ color: '#ff9800', fontSize: '0.75rem', marginLeft: '6px' }}>{bet.pickMultiplier.toFixed(2)}x</span>
                                    : null}
                                </Typography>
                                <Typography sx={{ color: '#cccccc', fontSize: '0.85rem' }}>
                                  {bet.leagueName} | {bet.eventShortName}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography sx={{ color: '#cccccc', fontSize: '0.85rem', textAlign: 'right' }}>
                                  Model%: <span style={{ color: '#ffffff' }}>{((bet.trueWin || 0) * 100).toFixed(1)}%</span>
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                  {/* Fixed Odds/Payout/EV Section at the Bottom */}
                  <Box
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      backgroundColor: '#222222',
                      borderTop: '2px solid #444',
                      p: 2,
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: '12px',
                      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    {slipWinInfo.conflicts.length > 0 && (
                      <Typography sx={{ color: '#ff9800', fontSize: '0.8rem', mb: 1 }}>
                        Same prop twice: {slipWinInfo.conflicts.join(', ')}
                      </Typography>
                    )}
                    <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500, mb: 1 }}>
                      All hit: <span style={{ color: '#ffffff' }}>
                        {betSlipBets.length ? `${(slipWinInfo.allHitProb * 100).toFixed(1)}%` : '-'}
                      </span>
                      <span style={{ color: '#888' }}> (fair odds {slipWinInfo.odds})</span>
                    </Typography>
                    <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500, mb: 1 }}>
                      Payout:{' '}
                      {slipWinInfo.payout != null
                        ? <span style={{ color: '#ffffff' }}>{slipWinInfo.payout.toFixed(2)}x</span>
                        : <span style={{ color: '#888', fontStyle: 'italic' }}>{betSlipBets.length ? (slipWinInfo.note || '-') : '-'}</span>}
                    </Typography>
                    {slipWinInfo.tiers.length > 0 && (
                      <Box sx={{ mb: 1, pl: 1 }}>
                        {slipWinInfo.tiers.map(t => (
                          <Typography key={t.hits} sx={{ color: '#bbbbbb', fontSize: '0.8rem' }}>
                            {t.hits}/{betSlipBets.length} hit: {t.payout.toFixed(2)}x · {(t.prob * 100).toFixed(1)}%
                          </Typography>
                        ))}
                      </Box>
                    )}
                    <Typography sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 500 }}>
                      Slip EV:{' '}
                      {slipWinInfo.ev != null
                        ? <span style={{ color: slipWinInfo.ev > 0 ? '#00ff00' : '#ff4444' }}>
                            {slipWinInfo.ev > 0 ? '+' : ''}{(slipWinInfo.ev * 100).toFixed(1)}%
                          </span>
                        : <span style={{ color: '#888', fontStyle: 'italic' }}>-</span>}
                      {slipWinInfo.ev != null && slipWinInfo.evIsFloor && (
                        <span style={{ color: '#888', fontSize: '0.75rem' }}> minimum — pool Extra Winnings can add more</span>
                      )}
                    </Typography>
                    <Typography sx={{ color: '#777', fontSize: '0.7rem', mt: 0.5 }}>
                      Assumes picks are independent. Payout tables checked {PAYOUTS_CHECKED} — confirm in the app.
                    </Typography>
                    {betSlipBets.length > 0 && (
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={trackSlip}
                        disabled={!userName || slipTracking || slipUntracked.length === 0}
                        sx={{
                          mt: 1.5,
                          color: '#ffffff',
                          borderColor: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          textTransform: 'none',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)', borderColor: '#ffffff' },
                          '&.Mui-disabled': slipUntracked.length === 0
                            ? { color: '#00cc00', borderColor: '#2e5e2e' }
                            : { color: '#888', borderColor: '#444' },
                        }}
                      >
                        {slipTracking
                          ? 'Tracking...'
                          : slipUntracked.length === 0
                            ? 'Slip tracked'
                            : slipUntracked.length === betSlipBets.length
                              ? `Track slip (${betSlipBets.length} ${betSlipBets.length === 1 ? 'pick' : 'picks'})`
                              : `Track ${slipUntracked.length} untracked ${slipUntracked.length === 1 ? 'pick' : 'picks'}`}
                      </Button>
                    )}
                    {bookFilter === 'PrizePicks' && betSlipBets.length >= 2 && (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={openSlipInApp}
                        disabled={slipLoading}
                        sx={{
                          mt: 1.5,
                          backgroundColor: '#7b2fbe',
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          textTransform: 'none',
                          '&:hover': { backgroundColor: '#6a28a6' },
                          '&.Mui-disabled': { backgroundColor: '#444', color: '#888' },
                        }}
                      >
                        {slipLoading ? 'Loading...' : `Open in PrizePicks (${betSlipBets.length} picks)`}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>

        <MarketsModal
          open={marketsOpen}
          onClose={closeMarkets}
          markets={marketsData}
          meta={marketsMeta}
        />
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default PickemDataGrid;